import {
  ContextualMenuItemType,
  DirectionalHint,
  IButtonProps,
  IconButton,
  Shimmer,
  Stack,
  TooltipHost
} from "@fluentui/react";
import {
  getNextColor,
  GroupedVerticalBarChart,
  IChartProps,
  IGroupedVerticalBarChartData,
  IVerticalBarChartDataPoint,
  VerticalBarChart
} from "@fluentui/react-charting";
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomHorizontalBarChart from "../BasicComponents/CustomHorizontalBarChart";
import FluentDonutChart from "../ChartComponents/FluentDountChart";
import { LineChartComponent } from "../ChartComponents/LineChartFluent";
import { CUSTOMER_VIEW_STYLES } from "../components/CustomerView/CustomerView.styles";
import { CustomShimmer } from "../helper/CustomShimmer";
import { selectViewport } from "../slices/UI.slice";
import { useAppSelector } from "../store/hooks";
import { getSumForNumberValues, groupBy } from "../Utils/js-common";
import CustomCard from "./CustomCard";
import { ChartDataOption, CustomCardWithChartsProps } from "./Interfaces/CustomCardWithCharts.interfaces";
import { CustomCardWithMetricItem } from "./Interfaces/CustomCardWithMetrics.interfaces";
import {
  classNames,
  contextualMenuStyles,
  customCardChartStyles,
  horizontalBarHeaderShimmerStyles,
  iconButtonStyles
} from "./Styles/CustomCardWithMetrics.styles";

export const CustomCardWithCharts = ({
  loading,
  metricFilters,
  onDataPointClick,
  onResetFilters,
  options,
  enableUrlParams = false // Default to false for backward compatibility
}: CustomCardWithChartsProps<any>) => {
  // Properties
  const customCardContainerRef = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const disableCustomCards = loading === false && !options;
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitializedRef = useRef(false);

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    return date;
  });
  const [previousMonthYearData, setPreviousMonthYearData] = useState<any[]>([]);
  const previousDataStore = useRef<Record<string, any[]>>({}).current;

  // Function to parse metric filters from URL (now returns array)
  const getMetricFiltersFromUrl = useCallback((): ChartDataOption<any>[] => {
    if (!enableUrlParams) return [];

    const metricFilterParam = searchParams.get("mf");
    if (!metricFilterParam) return [];

    try {
      const decodedFilter = atob(metricFilterParam);
      const parsed = JSON.parse(decodedFilter);
      // Handle both single filter (legacy) and array of filters
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.warn("Failed to parse URL metric filter parameters:", error);
      return [];
    }
  }, [searchParams, enableUrlParams]);

  // URL parameter update function (now handles multiple filters)
  const updateMetricFilterUrl = useCallback(
    (filterData: ChartDataOption<any>, shouldReplace: boolean = false) => {
      if (!enableUrlParams) return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (filterData && Object.keys(filterData).length > 0) {
          // Get existing filters
          const existingFilters = shouldReplace ? [] : getMetricFiltersFromUrl();

          // Create a serializable filter object
          const metricFilterData = {
            field: filterData.field,
            value: filterData.value,
            title: filterData.title,
            type: filterData.type
          };

          // Check if this filter already exists (to avoid duplicates)
          const filterExists = existingFilters.some(
            (filter) => filter.field === metricFilterData.field && filter.value === metricFilterData.value
          );

          let updatedFilters;
          if (filterExists) {
            // If filter exists, remove it (toggle behavior)
            updatedFilters = existingFilters.filter(
              (filter) => !(filter.field === metricFilterData.field && filter.value === metricFilterData.value)
            );
          } else {
            // Add new filter to existing ones
            updatedFilters = [...existingFilters, metricFilterData];
          }

          if (updatedFilters.length > 0) {
            // Convert array to JSON string first, then encode with btoa
            const jsonString = JSON.stringify(updatedFilters);
            const encodedValue = btoa(jsonString);
            newParams.set("mf", encodedValue);
          } else {
            newParams.delete("mf");
          }
        } else {
          newParams.delete("mf");
        }

        return newParams;
      });
    },
    [setSearchParams, getMetricFiltersFromUrl, enableUrlParams]
  );

  // Enhanced onDataPointClick wrapper
  const handleDataPointClick = useCallback(
    (filterData: ChartDataOption<any>) => {
      // Update URL parameters only if enabled
      if (enableUrlParams) {
        updateMetricFilterUrl(filterData);
      }

      // Call the original onDataPointClick prop
      onDataPointClick(filterData);
    },
    [onDataPointClick, updateMetricFilterUrl, enableUrlParams]
  );

  // Initialize metric filters from URL params on component mount
  useEffect(() => {
    if (loading || isInitializedRef.current || !enableUrlParams) return;

    const urlMetricFilter = getMetricFiltersFromUrl();
    if (urlMetricFilter && urlMetricFilter.length > 0) {
      try {
        // Apply each filter from URL
        for (const filter of urlMetricFilter) {
          onDataPointClick(filter);
        }
      } catch (error) {
        console.warn("Failed to apply URL metric filter:", error);
        // Clean up invalid metric filter params from URL
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("mf");
          return newParams;
        });
      }
    }

    isInitializedRef.current = true;
  }, [loading, getMetricFiltersFromUrl, onDataPointClick, setSearchParams, enableUrlParams]);

  const handleNextClick = () => {
    setStartDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 12);
      return newDate;
    });
  };

  const handlePreviousClick = () => {
    setStartDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 12);
      return newDate;
    });
  };

  // Events
  const getGroupedData = (key: string, data: any[]) => {
    const arr = Array.isArray(data) ? data : [];
    const filteredData = arr.filter((item) => item && item[key]);
    const groupedData = groupBy(filteredData, key);

    return Object.keys(groupedData)?.map((item) => ({
      name: item,
      value: groupedData[item].length
    }));
  };

  const buildMultiLineChartSeries = (
    field: string,
    data: any[],
    startDate: Date,
    monthsToAdd: number,
    seriesConfig: {
      legend: string;
      filterKey: string;
      filterValue: any;
      color: string;
    }[],
    option: any
  ) => {
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + monthsToAdd);

    return seriesConfig.map(({ legend, filterKey, filterValue, color }) => {
      const filteredData = getGroupedData(
        field,
        data.filter((x) => x[filterKey] === filterValue)
      )
        ?.map(({ name, value }) => ({
          x: new Date(new Date(name).toISOString()),
          y: value,
          onDataPointClick: () => handleDataPointClick({ ...option, value: name })
        }))
        .filter((item) => item.x >= startDate && item.x < endDate)
        .sort((a, b) => a.x.getTime() - b.x.getTime());

      const key = `${field}_${filterKey}_${filterValue}`;
      if (filteredData.length > 0) {
        previousDataStore[key] = filteredData;
      }

      return {
        legend,
        data: filteredData.length > 0 ? filteredData : previousDataStore[key] || [],
        color,
        lineOptions: { lineBorderWidth: "4" }
      };
    });
  };

  const handleResize = useCallback((width: number) => setWidth(width), []);
  const getHighlightedStatus = () => Object.keys(metricFilters).length;
  const horizontalBarHeader = (title: string | undefined, index: number, isLoading: boolean) => {
    const iconButtonProps: IButtonProps = {
      ariaLabel: "Applied Filters",
      menuProps: {
        directionalHint: DirectionalHint.bottomRightEdge,
        items: [
          {
            key: "selectedFiltersHeader",
            itemType: ContextualMenuItemType.Header,
            text: "Selected Filters",
            itemProps: {
              styles: {
                label: `${classNames.layoutText} ${classNames.headerLabel}`
              }
            }
          },
          {
            key: "selectedFiltersContext",
            onRender: (): ReactNode => {
              if (!Object.keys(metricFilters).length) {
                return <></>;
              }
              const formatValue = (value: string) => {
                const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
                if (value && dateRegex.test(value) && !isNaN(new Date(value).getTime())) {
                  return new Date(value).toISOString().substring(0, 10);
                }
                return value;
              };
              return (
                <div className={classNames.appliedFiltersContainer}>
                  {Object.keys(metricFilters)?.map((key) => (
                    <Fragment key={key}>
                      <div className={`${classNames.layoutText} ${classNames.nameText}`}>{key}</div>
                      <div className={classNames.essentialsSeparator}>:</div>
                      <div className={`${classNames.layoutText} ${classNames.secondaryText}`}>
                        {" "}
                        {formatValue(metricFilters[key].toString()) || "(empty)"}
                      </div>
                    </Fragment>
                  ))}
                </div>
              );
            }
          }
        ],
        styles: contextualMenuStyles
      },
      title: "Applied Filters"
    };

    if (isLoading) {
      return <Shimmer styles={horizontalBarHeaderShimmerStyles} />;
    }

    return (
      <Stack horizontal styles={{ root: { alignItems: "center", justifyContent: "space-between" } }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{title}</span>
          {options?.chartOptions.find((x: any) => x.tooltipDescription?.length > 0) && (
            <TooltipHost
              content={
                options.chartOptions.find((x: any) => x.tooltipDescription?.length > 0 && x.title == title)
                  ?.tooltipDescription
              }
              id="tooltip"
            >
              <IconButton
                iconProps={{ iconName: "Info" }}
                styles={{
                  root: {
                    marginTop: 5
                  }
                }}
              />
            </TooltipHost>
          )}
        </div>
        {getHighlightedStatus() ? (
          <Stack horizontal>
            <TooltipHost content="Clear all filters by clicking here." calloutProps={{ gapSpace: 0 }}>
              <IconButton
                iconProps={{ iconName: "ClearFilter" }}
                ariaLabel="Clear All Filters"
                onClick={() => {
                  // Clear URL parameters when resetting filters (only if enabled)
                  if (enableUrlParams) {
                    setSearchParams((prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.delete("mf");
                      return newParams;
                    });
                  }
                  onResetFilters();
                }}
              />
            </TooltipHost>
            <TooltipHost content="Filters already in use" calloutProps={{ gapSpace: 0 }}>
              <IconButton {...iconButtonProps} />
            </TooltipHost>
          </Stack>
        ) : (
          ""
        )}
      </Stack>
    );
  };
  const itemMemo: CustomCardWithMetricItem[] = useMemo(() => {
    if (disableCustomCards) return [];

    return (options || { chartOptions: [] }).chartOptions?.map((option) => {
      const { key, field, title, type, groupedData, id, legendName, seriesConfig } = option;
      const data = options?.filteredData ?? [];

      const yearsInData = groupedData.map((item) => new Date(item.name).getFullYear()).filter((year) => !isNaN(year));

      switch (type) {
        case "line": {
          const sixMonthsLater = new Date(startDate);
          sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 12);
          const dataByMonthYear = groupedData
            ?.map(({ name, value }) => ({
              x: new Date(new Date(name).toISOString()),
              y: value,
              onDataPointClick: () => handleDataPointClick({ ...option, value: name })
            }))
            .filter((item) => item.x >= startDate && item.x < sixMonthsLater)
            .sort((a, b) => a.x.getTime() - b.x.getTime());

          if (dataByMonthYear.length > 0) {
            setPreviousMonthYearData(dataByMonthYear);
          }
          const chartData: IChartProps = {
            lineChartData: [
              {
                legend: legendName,
                data: dataByMonthYear.length > 0 ? dataByMonthYear : previousMonthYearData,
                color: "#637cef",
                lineOptions: {
                  lineBorderWidth: "4"
                }
              }
            ]
          };

          // Ensure tick values update accordingly
          const newtickValues =
            dataByMonthYear.length > 0
              ? dataByMonthYear.filter((_, index) => index % 2 === 0)?.map((point) => point.x)
              : previousMonthYearData.filter((_, index) => index % 2 === 0)?.map((point) => point.x);

          return {
            component: () => (
              <>
                <LineChartComponent
                  data={chartData}
                  allowResize={true}
                  tickValues={newtickValues}
                  height={320}
                  width={width}
                  xAxisTitle={option.xaxisTitle}
                  yAxisTitle={option.yaxisTitle}
                  isLoading={loading}
                />
                {!loading  && chartData?.lineChartData?.find(item => item?.data?.length > 0) ? (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "-10px", gap: "15px" }}>
                    <IconButton
                      iconProps={{ iconName: "ChevronLeft" }}
                      title="Previous"
                      onClick={handlePreviousClick}
                      disabled={startDate.getFullYear() === Math.min(...yearsInData)}
                      styles={iconButtonStyles}
                    />
                    <IconButton
                      iconProps={{ iconName: "ChevronRight" }}
                      title="Next"
                      onClick={handleNextClick}
                      disabled={sixMonthsLater.getTime() >= new Date().getTime()}
                      styles={iconButtonStyles}
                    />
                  </div>
                ) : null}
              </>
            ),
            key,
            title
          };
        }
        case "multiline": {
          // multilineData should be an array of { legend, data: [{x, y, ...}], color }
          const sixMonthsLater = new Date(startDate);
          sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 12);
          const dataByMonthYear = groupedData
            ?.map(({ name, value }) => ({
              x: new Date(new Date(name).toISOString()),
              y: value,
              onDataPointClick: () => handleDataPointClick({ ...option, value: name })
            }))
            .filter((item) => item.x >= startDate && item.x < sixMonthsLater)
            .sort((a, b) => a.x.getTime() - b.x.getTime());

          const chartData = {
            lineChartData: buildMultiLineChartSeries(field, data, startDate, 12, seriesConfig, option)
          };

          // Ensure tick values update accordingly
          const newtickValues =
            dataByMonthYear.length > 0
              ? dataByMonthYear.filter((_, index) => index % 2 === 0)?.map((point) => point.x)
              : (Array.isArray(previousMonthYearData) ? previousMonthYearData : [])
                  .filter((_: any, index: number) => index % 2 === 0)
                  ?.map((point: any) => point.x);
          return {
            component: () => (
              <>
                <LineChartComponent
                  data={chartData}
                  allowResize={true}
                  tickValues={newtickValues}
                  height={320}
                  width={width}
                  xAxisTitle={option.xaxisTitle}
                  yAxisTitle={option.yaxisTitle}
                  isLoading={loading}
                />
                {!loading ? (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "-10px", gap: "20px" }}>
                    <IconButton
                      iconProps={{ iconName: "ChevronLeft" }}
                      title="Previous"
                      onClick={handlePreviousClick}
                      disabled={dataByMonthYear.length === 0}
                      styles={iconButtonStyles}
                    />
                    <IconButton
                      iconProps={{ iconName: "ChevronRight" }}
                      title="Next"
                      onClick={handleNextClick}
                      disabled={sixMonthsLater.getTime() >= new Date().getTime()}
                      styles={iconButtonStyles}
                    />
                  </div>
                ) : null}
              </>
            ),
            key,
            title
          };
        }
        case "donut": {
          const sortedGroupedData = groupedData?.slice().sort((a, b) => b.value - a.value);
          const chartData = sortedGroupedData?.map((item, index) => ({
            legend: item.name,
            data: item.value,
            color: item.colour || getNextColor(index),
            onClick: () => handleDataPointClick({ ...option, value: item.name })
          }));
          const valueInsideDonut = getSumForNumberValues(chartData?.map(({ data }) => data ?? 0));
          const donutWidth = width ? width : 300;
          const component = () =>
            !loading &&
            (!chartData ||
              !Array.isArray(chartData) ||
              chartData.length === 0 ||
              chartData.every((d) => !d.data || d.data === 0)) ? (
              <div
                style={{
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15
                }}
              >
                No data available to display, Try changing filters.
              </div>
            ) : (
              <>
                {option.bannerText && (
                  loading ? (
                    <Shimmer styles={{ root: { margin: "4px 8px" } }} />
                  ) : (
                    <div style={{ padding: "4px 8px", backgroundColor: "#FFF4CE", borderRadius: 4, fontSize: 12, color: "#797673", marginBottom: 4, textAlign: "center" }}>
                      <b>Note:</b> {option.bannerText}
                    </div>
                  )
                )}
                <FluentDonutChart
                data={{
                  chartTitle: "Donut Chart",
                  chartData: chartData
                }}
                height={200}
                isChartLoading={loading}
                styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                valueInsideDonut={valueInsideDonut}
                width={donutWidth}
              />
              </>
            );
          return {
            component,
            key,
            title
          };
        }
        case "verticalbar": {
          const points: IVerticalBarChartDataPoint[] = groupedData?.map((item, index) => ({
            x: item.name,
            y: item.value,
            color: getNextColor(index),
            onClick: () => handleDataPointClick({ ...option, value: item.name })
          }));

          const component = () =>
            loading ? (
              <CustomShimmer columnCount={4} rowCount={8} styles={{ root: { height: 300 } }} />
            ) : points && points.length > 0 ? (
              <VerticalBarChart
                chartTitle="Vertical Bar Chart"
                data={points}
                barWidth={100} // Adjust bar width for better responsiveness
                yAxisTickCount={5}
                hideLegend={true}
                enableReflow={true}
                strokeWidth={3}
                height={150}
                styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.verticalBarChart}
                width={width}
                maxBarWidth={24}
                wrapXAxisLables
              />
            ) : (
              <div
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15
                }}
              >
                No data available to display, Try changing filters.
              </div>
            );
          return {
            component,
            key,
            title
          };
        }
        case "groupedverticalbar": {
          const points: IGroupedVerticalBarChartData[] = loading
            ? [{ name: "", series: [] }]
            : groupedData?.map((item) => ({
                name: item.name,
                series: seriesConfig?.map(({ legend, filterKey, color }) => {
                  const value = item.value.get(filterKey) || 0;
                  return {
                    key: filterKey,
                    data: value,
                    color: color,
                    legend: legend,
                    xAxisCalloutData: legend,
                    yAxisCalloutData: `${value}`,
                    onClick: () => handleDataPointClick({ ...option, value: item.name })
                  };
                })
              }));

          const component = () =>
            loading ? (
              <CustomShimmer rowCount={8} styles={{ root: { maxHeight: 300 } }} />
            ) : points?.length ? (
              <GroupedVerticalBarChart
                chartTitle="Grouped Vertical Bar chart styled example"
                data={points}
                yAxisTickCount={6}
                strokeWidth={5}
                height={200}
                width={width}
                barwidth={50}
                wrapXAxisLables
                xAxisInnerPadding={1}
                xAxisOuterPadding={0.8}
                hideLegend={false}
                enableReflow={true}
                hideLabels={true}
              />
            ) : (
              <div style={{ padding: "10px" }}>No data to display</div>
            );
          return {
            component,
            key,
            title
          };
        }
        case "horizontalbar": {
          const sortedData = groupedData?.sort((a, b) => b.value - a.value).slice(0, 10); // Limit to top 10 items

          const formattedData = sortedData?.map(({ name, value }) => {
            return {
              chartTitle: name,
              chartData: [
                {
                  legend: name || "",
                  horizontalBarChartdata: {
                    x: Number(value),
                    y: 10000 // Adjust the y value based on your logic
                  },
                  onClick: () => handleDataPointClick({ ...option, value: name }),
                  color: getNextColor(id) // Updated color
                }
              ]
            };
          });

          const component = () =>
            sortedData && (
              <div style={{ padding: "10px" }}>
                <CustomHorizontalBarChart data={formattedData} minHeight={300} itemsPerPage={5} isLoading={loading} />
              </div>
            );

          return {
            component,
            key,
            title
          };
        }
        default:
          break;
      }

      return {
        key: "",
        title: "",
        component: () => <></>
      };
    });
  }, [options, loading, width, startDate, handleDataPointClick]);

  // Hookes
  useEffect(() => {
    if (customCardContainerRef.current) {
      handleResize(customCardContainerRef.current?.offsetWidth);
    }
  }, [resizedWidth, customCardContainerRef]);

  return itemMemo?.map(({ component, key, title }, index) => (
    <CustomCard
      key={key}
      cardHeaderTitle={horizontalBarHeader(title, index, loading)}
      styles={customCardChartStyles}
      kustoLink={options?.chartOptions[index]?.kustoLink}
      isLoading={loading}
      chartTitle={options?.chartOptions[index]?.title}
    >
      <div ref={customCardContainerRef} className={classNames.chartContainer}>
        {component()}
      </div>
    </CustomCard>
  ));
};
